import React, { useCallback } from 'react';
import clsx from 'clsx';

import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';

import { Field, FieldInputProps } from 'react-final-form';

import FormError from './FormError';
import './PhoneInput.scss';
import FormErrorText from './FormErrorText';

type ErrorDisplayType = 'experience' | 'basic';

interface IPhoneInput
  extends Partial<Pick<FieldInputProps<any, HTMLInputElement>, 'onChange'>> {
  name: string;
  type?: string;
  label?: string;
  validation?: (value: any) => any;
  className?: string;
  defaultCountry?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, isValid: boolean) => void;
  errorDisplayType?: ErrorDisplayType;
}

const textRegex = /([A-Za-zĄą-Žž])+/;

const PhoneInput: React.FC<IPhoneInput> = ({
  name,
  label,
  type,
  validation,
  className,
  defaultCountry,
  onBlur,
  errorDisplayType = 'experience',
  ...rest
}) => {
  const renderErrorComponent = useCallback(
    (errorText?: string) => {
      if (errorDisplayType === 'basic') {
        return <FormErrorText errorText={errorText} />;
      }
      if (errorDisplayType === 'experience') {
        return <FormError errorText={errorText} />;
      }
    },
    [errorDisplayType],
  );

  return (
    <div className={clsx(`phone-input-block`, className)}>
      <Field name={name} validate={validation}>
        {({ input, meta }) => {
          const isError = meta.error && meta.touched;
          return (
            <>
              {label && (
                <label
                  htmlFor={name}
                  className={`phone-input-block__label ${
                    isError && errorDisplayType === 'basic' ? 'text-danger' : ''
                  }`}
                >
                  {label}
                </label>
              )}
              <div className='phone-input-block__input-container'>
                <IntlTelInput
                  nationalMode={false}
                  type={type}
                  defaultCountry={defaultCountry}
                  inputClassName={clsx(
                    'phone-input-block__input',
                    isError && 'phone-input-block__input--error',
                  )}
                  {...rest}
                  {...input}
                  onPhoneNumberBlur={(
                    isValid,
                    _value,
                    _selectedCountryData,
                    _fullNumber,
                    _extension,
                    event,
                  ) => {
                    onBlur?.(event, isValid);
                    input?.onBlur?.(event);
                  }}
                  onPhoneNumberChange={(isValid, val, _selected, fullNumber) => {
                    if (textRegex.test(val)) {
                      return;
                    }
                    // if number isValid, we can assume it was formatted and it can be passed to the onChange
                    rest?.onChange?.(isValid ? fullNumber : val);
                    input?.onChange?.(isValid ? fullNumber : val);
                  }}
                />
                {isError && renderErrorComponent(meta.error)}
              </div>
            </>
          );
        }}
      </Field>
    </div>
  );
};

export default PhoneInput;
